import type { DOMNode } from 'html-react-parser';
import type { ReactElement } from 'react';

import StoreButtonsTemplate from './AppStoreButtons';
import DashTemplate from './Dash';
import FillSpacerTemplate from './FillSpacer';
import FormButtonTemplate from './FormButton';
import ImageAndTextListTemplate from './ImageAndTextList';
import PromoBoxTemplate from './PromoBox';
import ShareLinksTemplate from './ShareLinks';
import SmallDashTemplate from './SmallDash';
import ThreeColumnsTemplate from './ThreeColumns';
import { TwoByTwoGridTemplate, TwoByTwoGridTemplateWithBorder } from './TwoByTwoGrid';
import TwoColumnsTemplate from './TwoColumns';

/**
 * Enum of all available CKEditor template types.
 *
 * Makes it easier to read and edit the ids.
 */

enum TEMPLATES_TYPE {
  Dash = 'ckeditor-template-dash',
  SmallDash = 'ckeditor-template-small-dash',
  FillSpacer = 'ckeditor-template-fill-spacer',
  FormButton = 'ckeditor-template-form-button',
  TwoColumns = 'ckeditor-template-2-cols',
  ThreeColumns = 'ckeditor-template-3-cols',
  ShareLinks = 'ckeditor-template-share-links',
  TwoByTwoGrid = 'ckeditor-template-2x2-grid',
  ImageAndTextList = 'ckeditor-template-img-text-list',
  TwoByTwoGridWithBorder = 'ckeditor-template-2x2-grid-border',
  StoreButtons = 'ckeditor-template-app-buttons',
  PromoBox = 'ckeditor-template-promo-box',
}

/**
 * Props for the CKEditor template components.
 */

type TemplateProps = {
  nodes: DOMNode[];
  attribs: {
    [name: string]: string;
  };
};

/**
 * Generic type for the CKEditor tenmplate components.
 */

export type Template = (props: TemplateProps) => ReactElement;

/**
 * Factory of CKEditor templates.
 */

const templates: Record<string, Template> = {
  [TEMPLATES_TYPE.Dash]: DashTemplate,
  [TEMPLATES_TYPE.SmallDash]: SmallDashTemplate,
  [TEMPLATES_TYPE.FillSpacer]: FillSpacerTemplate,
  [TEMPLATES_TYPE.FormButton]: FormButtonTemplate,
  [TEMPLATES_TYPE.TwoColumns]: TwoColumnsTemplate,
  [TEMPLATES_TYPE.ThreeColumns]: ThreeColumnsTemplate,
  [TEMPLATES_TYPE.ShareLinks]: ShareLinksTemplate,
  [TEMPLATES_TYPE.TwoByTwoGrid]: TwoByTwoGridTemplate,
  [TEMPLATES_TYPE.ImageAndTextList]: ImageAndTextListTemplate,
  [TEMPLATES_TYPE.TwoByTwoGridWithBorder]: TwoByTwoGridTemplateWithBorder,
  [TEMPLATES_TYPE.StoreButtons]: StoreButtonsTemplate,
  [TEMPLATES_TYPE.PromoBox]: PromoBoxTemplate,
};

/**
 * Helper function to get a template component by its id.
 *
 * @param id - The template id.
 *
 * @returns The template component or null if not found.
 */

export const getTemplate = (id: string) => {
  return templates[id] || null;
};
